import { Box, breakpoints, Icon } from '@gassan-ui'
import Portal from '@reach/portal'
import React, { FC, useEffect, useState, useRef } from 'react'
import { FilterAction, FilterState, Filters } from '../../reducer'
import * as S from './elements'
import Sort from './Sort'
import { useBodyScrollLock } from '@lib/hooks/useBodyScrollLock'
import { AnimatePresence } from 'framer-motion'
import { useLocalization } from '@components/Localization'
import FilterOverlay from './FilterOverlay'
import {
  Filters_filters_articleAttributeFilters,
  Filters_filters_brandFilters,
  Filters_filters_brandCollectionFilters
} from '@graphql/__generated__/Filters'
import { FloatingServiceMenu } from '@components/Page'
import { useBreakpoint } from '@lib/hooks/useBreakpoint'

type Props = FilterState & {
  dispatch: React.Dispatch<FilterAction>
  fixedFilters: Filters
  attributes: Filters_filters_articleAttributeFilters[]
  brandOptions: Filters_filters_brandFilters[]
  collectionOptions: Filters_filters_brandCollectionFilters[]
}

export type Variant = 'filter' | 'sort' | null

const MobileFilters: FC<Props> = ({
  sort,
  dispatch,
  fixedFilters,
  attributes,
  brandOptions,
  collectionOptions,
  ...state
}) => {
  const [active, setActive] = useState<Variant>(null)
  const { lang } = useLocalization()
  const [shouldRender, setShouldRender] = useState(false)
  const ref = useRef<HTMLDivElement>()
  const isTablet = useBreakpoint(breakpoints.small)
  useEffect(() => {
    const mediaQuery = window.matchMedia(`(min-width: ${breakpoints.large})`)

    function handleMediaQueryChange(mql) {
      setShouldRender(!mql.matches)
    }

    handleMediaQueryChange(mediaQuery)
    mediaQuery.addListener(handleMediaQueryChange)
    return () => {
      mediaQuery.removeListener(handleMediaQueryChange)
    }
  }, [])

  const isActive = active !== null
  useBodyScrollLock(ref, isActive)

  if (!shouldRender) {
    return <FloatingServiceMenu />
  }

  let total = Object.keys(state.filters).reduce((prev, key) => {
    const slice = state.filters[key]
    if (!fixedFilters[parseInt(key, 10)]) {
      return prev + slice.length
    }
    return prev
  }, 0)
  total = total + state.brands.length + state.collections.length

  return (
    <>
      <Portal>
        <AnimatePresence>
          <S.Grid
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ duration: 0.1 }}
            key="buttons"
          >
            <S.Col>
              <S.Button
                onClick={() => setActive(active === 'filter' ? null : 'filter')}
                isActive={active === 'filter'}
              >
                Filter
                {total > 0 ? (
                  <S.Indicator>{total}</S.Indicator>
                ) : (
                  <Icon icon={active === 'filter' ? 'settings-alt' : 'settings'} />
                )}
              </S.Button>
            </S.Col>
            <Box backgroundColor="shades.200"></Box>
            <S.Col>
              <S.Button
                onClick={() => setActive(active === 'sort' ? null : 'sort')}
                isActive={active === 'sort'}
              >
                {lang === 'nl' ? 'Sorteer' : 'Sort'}
                <Icon
                  icon="chevron-down-small"
                  style={{ transform: `rotate(${active === 'sort' ? '180deg' : '0'})` }}
                />
              </S.Button>
            </S.Col>
          </S.Grid>

          {active === 'sort' && (
            <Sort sort={sort} dispatch={dispatch} onRequestClose={() => setActive(null)} />
          )}
          {active === 'filter' && (
            <FilterOverlay
              dispatch={dispatch}
              onRequestClose={() => setActive(null)}
              attributes={attributes}
              fixedFilters={fixedFilters}
              ref={ref}
              brandOptions={brandOptions}
              collectionOptions={collectionOptions}
              totalAppliedFitlers={total}
              {...state}
            />
          )}
        </AnimatePresence>
      </Portal>
      {active === null && <FloatingServiceMenu moveElementUp={isTablet ? '3.5rem' : '2.5rem'} />}
    </>
  )
}

export default MobileFilters
